import { FC, useCallback, useEffect, useMemo, memo } from "react";
import query from "../../query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import mutation from "../../mutation";
import {
  BetPriceType,
  BetType,
  SportType,
  TabelType,
} from "../../types/common";
import NewSportTable from "../../components/sport-table";
import { format } from "../../components/sport-table/hooks";
import { Markets } from "../../components/sport-table/types/football_table";
import { flatten } from "lodash";
import useBetInfo from "../../hooks/useBetInfo";
import useSpecial from "./useSpecial";
import { useTranslation } from "react-i18next";
import { Member } from "../../types/member";
import produce from "immer";
import { getSportFeedQuery, SportKey } from "../../query/sportFeedFactory";
import { MatchSharePayload } from "../../types/table";
import { CONFIG } from "../../config";

export type NewSportTableContainerProps = {
  sport:
    | "football"
    | "basketball"
    | "volleyball"
    | "tabletennis"
    | "handball"
    | "icehockey"
    | "tennis"
    | "snooker"
    | "baseball"
    | "rugby"
    | "americanfootball"
    | "cricket"
    | "boxing"
    | "bull_fight"
    | "cock_fight"
    | "badminton";
  type: TabelType;
  searchTerm: string;
  onTeamsLoad: (teams: string[]) => void;
  leagueFilterState?: {
    isAllSelected: boolean;
    selectedLeagues: string[];
  };
  onLeaguesLoad?: (
    leagues: { label: string; value: string; favorite?: boolean }[],
  ) => void;
  onShareMatch?: (match: MatchSharePayload) => void;
  sharedMatchId?: string;
  pvd?: string;
};

const NewSportTableContainer: FC<NewSportTableContainerProps> = ({
  sport,
  type,
  searchTerm,
  onTeamsLoad,
  leagueFilterState,
  onLeaguesLoad,
  onShareMatch,
  sharedMatchId,
  pvd,
}) => {
  const profileQuery = query.profile();
  const profile = useQuery(profileQuery.key, profileQuery.queryFn);
  const { focusMatchId, setFocusMatchId, ...specialPricesFeed } = useSpecial(
    sport,
    type,
    pvd,
  );

  const betInfo = useBetInfo();
  const oddsTypeQuery = query.oddsType();
  const oddsType = useQuery(oddsTypeQuery.key, oddsTypeQuery.queryFn, {
    refetchOnMount: false,
    keepPreviousData: true,
  });
  const queryClient = useQueryClient();
  const updateBetTicketInfoPayload = useMutation(
    mutation.updateBetTicketInfoPayload,
    {
      onSuccess: (payload) => {
        queryClient.setQueryData(["betTicketInfoPayload"], payload);
      },
    },
  );

  const betTypeQuery = query.betType();
  const betType = useQuery(betTypeQuery.key, betTypeQuery.queryFn, {
    refetchOnMount: false,
    keepPreviousData: true,
  });

  useEffect(() => {
    const shouldPreserveComboSelections =
      CONFIG.ENV === "develop" && betType.data === "comboStep";

    if (!shouldPreserveComboSelections) {
      updateBetTicketInfoPayload.mutate([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const feedType = type as "today" | "live" | "early";
  const resolvedBetType = betType.data ?? "normal";

  const sportFeedQuery = getSportFeedQuery(
    sport as SportKey,
    feedType,
    resolvedBetType,
    pvd,
  );

  const sportFeed = useQuery(sportFeedQuery.key, sportFeedQuery.queryFn);
  const { i18n } = useTranslation();
  const data = useMemo(
    () =>
      format(
        sportFeed.data,
        type,
        i18n.language,
        profile.data?.favoriteLeagues?.map((league) => `${league.id}`),
      ),
    [sportFeed.data, type, i18n.language, profile.data],
  );

  const favoriteLeagueIdSet = useMemo(() => {
    const ids = profile.data?.favoriteLeagues?.map((l) => String(l.id)) ?? [];
    return new Set(ids);
  }, [profile.data?.favoriteLeagues]);

  const leagueFilteredData = useMemo(() => {
    if (!data) return data;
    if (!leagueFilterState) return data;

    const { isAllSelected, selectedLeagues } = leagueFilterState;
    return data.filter((league) => {
      const id = `${league.id}`;
      return isAllSelected ?
          !selectedLeagues.includes(id)
        : selectedLeagues.includes(id);
    });
  }, [data, leagueFilterState]);

  useEffect(() => {
    if (data) {
      if (data.length > 0) {
        const allLeagueNames = data.map((league) => league.league.en || "");
        const allTeamNames = data.flatMap((league) =>
          league.matches.flatMap((match) => [
            match.home,
            match.home_en,
            match.home_th,
            match.away,
            match.away_en,
            match.away_th,
          ]),
        );

        const combinedList = [...allLeagueNames, ...allTeamNames].filter(
          Boolean,
        );
        const uniqueItems = Array.from(new Set(combinedList));
        onTeamsLoad(uniqueItems);

        const leagues = data.map((league) => ({
          label: league.league.en,
          value: String(league.id),
          favorite: favoriteLeagueIdSet.has(String(league.id)),
        }));
        onLeaguesLoad?.(leagues);
      } else {
        onTeamsLoad([]);
        onLeaguesLoad?.([]);
      }
    }
  }, [data, onTeamsLoad, onLeaguesLoad, sport, type, favoriteLeagueIdSet]);

  const filteredData = useMemo(() => {
    const source = leagueFilteredData || [];
    if (!source.length) return source;

    if (sharedMatchId) {
      const result: typeof source = [];

      source.forEach((league) => {
        const filteredMatches = league.matches.filter(
          (match) => String(match.id) === String(sharedMatchId),
        );

        if (filteredMatches.length > 0) {
          result.push({ ...league, matches: filteredMatches });
        }
      });

      return result;
    }

    if (!searchTerm?.trim()) {
      return source;
    }

    const lowercasedSearchTerm = searchTerm.toLowerCase();
    const result: typeof source = [];

    source.forEach((league) => {
      const leagueName = league.league.en || "";
      const leagueNameMatches = leagueName
        .toLowerCase()
        .includes(lowercasedSearchTerm);

      if (leagueNameMatches) {
        result.push(league);
      } else {
        const filteredMatches = league.matches.filter(
          (match) =>
            match.home.toLowerCase().includes(lowercasedSearchTerm) ||
            match.away.toLowerCase().includes(lowercasedSearchTerm) ||
            match.home_en.toLowerCase().includes(lowercasedSearchTerm) ||
            match.away_en.toLowerCase().includes(lowercasedSearchTerm) ||
            match.home_th.toLowerCase().includes(lowercasedSearchTerm) ||
            match.away_th.toLowerCase().includes(lowercasedSearchTerm),
        );

        if (filteredMatches.length > 0) {
          result.push({ ...league, matches: filteredMatches });
        }
      }
    });

    return result;
  }, [leagueFilteredData, searchTerm, sharedMatchId]);

  const handleShareClick = useCallback(
    ({ leagueId, matchId }: { leagueId: string; matchId: string }) => {
      if (!onShareMatch || !filteredData?.length) return;

      const league = filteredData.find(
        (l) => String(l.id) === String(leagueId),
      );
      if (!league) return;

      const match = league.matches.find(
        (m) => String(m.id) === String(matchId),
      );
      if (!match) return;

      const payload: MatchSharePayload = {
        matchId: String(match.id),
        leagueId: String(league.id),
        leagueName: league.league.en,
        homeName: match.home,
        awayName: match.away,
        kickoffTime: (match as any).kickoffTime ?? (match as any).match_time,
        sportType: sport.toUpperCase() as SportType,
        tableType: type,
      };

      onShareMatch(payload);
    },
    [filteredData, onShareMatch, sport, type],
  );

  const pinFavorite = useMutation(mutation.pinFavorite, {
    onSuccess: (payload) => {
      const member = queryClient.getQueryData<Member>(["profile"]);
      const newBetTicketInfoPayload = produce(member, (draft) => {
        if (draft) {
          draft.favoriteTeams = payload.favoriteTeams;
          draft.favoriteLeagues = payload.favoriteLeagues;
        }
      });

      queryClient.setQueryData(["profile"], newBetTicketInfoPayload);
    },
  });

  const onClickPinFavorite = useCallback(
    (payload: { id: string; name: string }, type: "LEAGUE" | "TEAM") => {
      if (profile.data?._id) {
        pinFavorite.mutateAsync({
          memberId: profile.data._id,
          update: payload,
          type,
        });
      }
    },
    [pinFavorite, profile.data],
  );

  const onClickPrice = useCallback(
    ({
      outcomeId,
      marketId,
      sp,
      matchId,
      leagueId,
      option = 0,
      minParlay,
    }: {
      outcomeId: string;
      marketId: string;
      sp: string;
      matchId: string;
      leagueId: string;
      option: number;
      minParlay?: number;
    }) => {
      if (!filteredData?.length) {
        return;
      }

      const foundLeague = filteredData?.find((league) => {
        return `${league.id}` === `${leagueId}`;
      });
      if (!foundLeague) {
        return;
      }
      const foundMatch = foundLeague.matches.find((match) => {
        return `${match.id}` === `${matchId}`;
      });
      const bet_prices = foundMatch?.bet_prices?.map((bet_price, riskLevel) => {
        return Object.keys(bet_price).map((key) => {
          return { ...bet_price[key as keyof Markets], market: key, riskLevel };
        });
      });

      const format_bet_prices = flatten(bet_prices);

      const found_bet_price = format_bet_prices.find(
        (format_bet_price: any) => {
          return (
            `${format_bet_price.sp}` === `${sp}` &&
            `${format_bet_price.market_id}` === `${marketId}` &&
            (`${format_bet_price.home_outcome_id}` === `${outcomeId}` ||
              `${format_bet_price.away_outcome_id}` === `${outcomeId}` ||
              `${format_bet_price.draw_outcome_id}` === `${outcomeId}` ||
              `${format_bet_price.over_outcome_id}` === `${outcomeId}` ||
              `${format_bet_price.under_outcome_id}` === `${outcomeId}` ||
              `${format_bet_price.odd_outcome_id}` === `${outcomeId}` ||
              `${format_bet_price.even_outcome_id}` === `${outcomeId}`)
          );
        },
      );

      betInfo.push({
        leagueId,
        matchId,
        betType: `${found_bet_price?.market}` as BetPriceType,
        sportType: sport.toUpperCase() as SportType,
        table: type,
        type: `${betType.data}` as BetType,
        home: `${foundMatch?.home}`,
        away: `${foundMatch?.away}`,
        isHomeGood: `${(found_bet_price as any).home_hdp}`.includes("-"),
        isAwayGood: `${(found_bet_price as any).away_hdp}`.includes("-"),
        league: foundLeague.league.en,
        fhid: `${foundMatch?.home_flag_id}`,
        faid: `${foundMatch?.away_flag_id}`,
        fhimg: `${foundMatch?.home_flag_img}`,
        faimg: `${foundMatch?.away_flag_img}`,
        outcomeId,
        specifiers: sp,
        marketId,
        riskLevel: (found_bet_price as any).riskLevel,
        option: option as any,
        pvd,
        minParlay,
      });
    },

    [betInfo, betType.data, filteredData, sport, type, pvd],
  );

  const onClickSpecialPrice = useCallback(
    ({
      outcomeId,
      marketId,
      sp,
      matchId,
      leagueId,
      option = 0,
    }: {
      outcomeId: string;
      marketId: string;
      sp: string;
      matchId: string;
      leagueId: string;
      option: number;
    }) => {
      if (!filteredData?.length) {
        return;
      }

      const foundLeague = filteredData?.find((league) => {
        return `${league.id}` === `${leagueId}`;
      });
      if (!foundLeague) {
        return;
      }
      const foundMatch = foundLeague.matches.find((match) => {
        return `${match.id}` === `${matchId}`;
      });

      if (!foundMatch) {
        return;
      }

      const market = Object.keys(specialPricesFeed.data).find((key) => {
        const value = specialPricesFeed.data?.[key];

        return value?.[0]?.marketId === marketId;
      });

      betInfo.push({
        leagueId,
        matchId,
        betType: market as BetPriceType,
        sportType: sport.toUpperCase() as SportType,
        table: type,
        type: `${betType.data}` as BetType,
        home: `${foundMatch?.home}`,
        away: `${foundMatch?.away}`,
        isHomeGood: false,
        isAwayGood: false,
        league: foundLeague.league.en,
        fhid: `${foundMatch?.home_flag_id}`,
        faid: `${foundMatch?.away_flag_id}`,
        outcomeId,
        specifiers: sp,
        marketId,
        riskLevel: 0,
        option: option as any,
        pvd,
      });
    },
    [
      betInfo,
      betType.data,
      filteredData,
      specialPricesFeed.data,
      sport,
      type,
      pvd,
    ],
  );

  return (
    <NewSportTable
      sport={sport}
      tableType={type}
      isFlagVisible={sport === "football"}
      isLive={type === "live"}
      hasMl={[
        "tabletennis",
        "handball",
        "baseball",
        "cricket",
        "rugby",
        "americanfootball",
        "tennis",
        "badminton",
        "icehockey",
        "volleyball",
        "cock_fight",
        "bull_fight",
        "boxing",
      ].includes(sport)}
      oddsType={oddsType.data}
      onClickPrice={onClickPrice}
      onClickPinFavorite={onClickPinFavorite}
      data={filteredData}
      isLoading={sportFeed.isLoading}
      specialData={specialPricesFeed.data}
      isLoadingSpecialData={specialPricesFeed.isLoading}
      focusMatchId={focusMatchId}
      onClickMore={setFocusMatchId}
      onClickSpecialPrice={onClickSpecialPrice}
      onClickShare={onShareMatch ? handleShareClick : undefined}
    />
  );
};

export default memo(NewSportTableContainer);
